/**
 * Política de incógnitas en Completar — cada ejercicio debe ser resoluble.
 * Regla: con N incógnitas numéricas, al menos N−1 datos visibles fuera de corchetes.
 */

export interface PercentVariationScenario {
  item: string;
  originalPrice: number;
  newPrice: number;
  currencySymbol?: string;
}

/** Escenarios neutros para reparación — sin locale ni lugar fijo. */
export const PERCENT_VARIATION_SCENARIOS: PercentVariationScenario[] = [
  { item: 'producto', originalPrice: 40, newPrice: 50 },
  { item: 'artículo', originalPrice: 80, newPrice: 92 },
  { item: 'bien', originalPrice: 120, newPrice: 138 },
  { item: 'servicio', originalPrice: 95, newPrice: 104.5 },
  { item: 'paquete', originalPrice: 200, newPrice: 180 },
];

function formatAmount(value: number, symbol = '$'): string {
  const n = Math.round(value * 100) / 100;
  const text = Number.isInteger(n) ? String(n) : String(n);
  return `${symbol}${text}`;
}

export function calcPercentVariation(original: number, newPrice: number): number {
  const pct = ((newPrice - original) / original) * 100;
  return Math.round(pct * 10) / 10;
}

function formatPercentAnswer(pct: number): string {
  const label = pct % 1 === 0 ? String(pct) : String(pct).replace('.', ',');
  return `${label}%`;
}

function scenarioIndex(seed: string, fallback = 0): number {
  if (!seed) return fallback % PERCENT_VARIATION_SCENARIOS.length;
  let hash = 0;
  for (let i = 0; i < seed.length; i += 1) {
    hash = (hash + seed.charCodeAt(i) * (i + 1)) % PERCENT_VARIATION_SCENARIOS.length;
  }
  return hash;
}

export function buildPercentVariationCompletarText(seed = '', scenarioIndexOverride?: number): string {
  const idx =
    scenarioIndexOverride != null
      ? scenarioIndexOverride % PERCENT_VARIATION_SCENARIOS.length
      : scenarioIndex(seed);
  const s = PERCENT_VARIATION_SCENARIOS[idx]!;
  const sym = s.currencySymbol ?? '$';
  const pct = formatPercentAnswer(calcPercentVariation(s.originalPrice, s.newPrice));
  return (
    `El precio inicial de un ${s.item} era ${formatAmount(s.originalPrice, sym)} ` +
    `y el nuevo precio es ${formatAmount(s.newPrice, sym)}. ` +
    `La variación porcentual es de [${pct}].`
  );
}

function stripBlanks(text: string): string {
  return text.replace(/\[[^\]]*\]/g, ' ');
}

function countBlanks(text: string): number {
  return (text.match(/\[[^\]]+\]/g) || []).length;
}

/** Montos visibles (fuera de corchetes). */
export function countVisibleCurrencyValues(text: string): number {
  const visible = stripBlanks(text);
  const matches = visible.match(
    /[$€£¥₡]\s*[\d.,]+|\b[\d.,]+\s*(?:usd|eur|pesos|units?)\b|\b\d+(?:[.,]\d+)?\b/gi,
  );
  return matches?.length ?? 0;
}

export function isPercentVariationContext(text: string): boolean {
  return (
    /variaci[oó]n\s+porcentual|porcentual|percent\s+variation|precio\s+(?:original|inicial|nuevo|final)|initial\s+price|new\s+price/i.test(
      text,
    ) || (/costaba|cuesta/i.test(text) && /%/.test(text))
  );
}

/**
 * Detecta ejercicios de variación % con demasiadas incógnitas y sin datos dados.
 */
export function isUnsolvablePercentCompletar(text: string): boolean {
  if (!isPercentVariationContext(text)) return false;
  const blanks = countBlanks(text);
  if (blanks < 2) return false;
  const visible = countVisibleCurrencyValues(text);
  return visible < blanks - 1;
}

export interface RepairCompletarOptions {
  title?: string;
  printTitle?: string;
  topic?: string;
  index?: number;
  subjectTag?: string;
  isGuide?: boolean;
  guideContext?: string;
  /** Si false, nunca reemplaza por plantilla de variación %. */
  allowPercentRepair?: boolean;
}

export function isExplicitMathPercentTopic(subjectTag = '', topic = ''): boolean {
  return /porcentaj|variaci[oó]n\s+porcentual|fracci|decimal|ratio|proporci|matem[aá]tica/i.test(
    `${subjectTag} ${topic}`.toLowerCase(),
  );
}

export function isHumanitiesGuideSubject(subjectTag = '', topic = ''): boolean {
  if (isExplicitMathPercentTopic(subjectTag, topic)) return false;
  const probe = `${subjectTag} ${topic}`.toLowerCase();
  if (/matem|f[ií]sica|qu[ií]mica|biolog|ciencias?\s+naturales|algebra|geometr/i.test(probe)) {
    return false;
  }
  return true;
}

/** Ejercicio tipo cálculo de % (no comprensión lectora del contexto). */
export function looksLikePercentDrillCompletar(text: string): boolean {
  const t = text.toLowerCase();
  if (
    /variaci[oó]n\s+porcentual|p[eé]rdida\s+en\s+porcentaje|porcentaje\s+fue|calcule?\s+(?:el|la)\s+\d*\s*%/.test(
      t,
    )
  ) {
    return true;
  }
  if (/equivale\s+a\s+un\s+\d+[,.]?\d*\s*%\s*(?:menos|m[aá]s)/i.test(text)) return true;
  if (countBlanks(text) === 1 && /%\]/.test(text) && countVisibleCurrencyValues(text) >= 1) return true;
  if (countBlanks(text) === 1 && /\[\s*\d*\s*%\s*\]/.test(text) && /\$\s*[\d.,]+|[\d.,]+\s*millones/i.test(text)) {
    return true;
  }
  return false;
}

function sentenceToContextualCompletar(sentence: string): string | null {
  const trimmed = sentence.trim();
  if (trimmed.length < 24) return null;

  const withYear = trimmed.replace(/\b((?:1[5-9]|20)\d{2})\b/, '[$1]');
  if (withYear !== trimmed && countBlanks(withYear) === 1) return withYear;

  const properName = trimmed.match(
    /\b([A-ZÁÉÍÓÚÑ][a-záéíóúñ]+(?:\s+(?:de|del|la|y)\s+)?[A-ZÁÉÍÓÚÑ][a-záéíóúñ]+(?:\s+[A-ZÁÉÍÓÚÑ][a-záéíóúñ]+)?)\b/,
  );
  if (properName?.[1] && properName[1].length >= 4) {
    const converted = trimmed.replace(properName[1], `[${properName[1]}]`);
    if (countBlanks(converted) === 1) return converted;
  }

  const termed = trimmed.match(
    /(?:llamad[oa]s?|conocid[oa]s?\s+como|denominad[oa]s?|denominada)\s+[«""]?([^«"".,;]{3,48})[»""]?/i,
  );
  if (termed?.[1]) {
    const term = termed[1].trim();
    const converted = trimmed.replace(term, `[${term}]`);
    if (countBlanks(converted) === 1) return converted;
  }

  return null;
}

/** Deriva un completar desde prosa de info_block (fechas, nombres, términos). */
export function buildContextualCompletarFromGuide(context: string, index = 0): string | null {
  const sentences = context
    .split(/(?<=[.!?])\s+|\n+/)
    .map((s) => s.trim())
    .filter((s) => s.length >= 30);

  const candidates: string[] = [];
  for (const sentence of sentences) {
    const converted = sentenceToContextualCompletar(sentence);
    if (converted) candidates.push(converted);
  }
  if (!candidates.length) return null;
  return candidates[index % candidates.length]!;
}

export function collectGuideContextFromBlocks(
  blocks: Array<{ mode?: string; payload?: Record<string, unknown> }>,
): string {
  return blocks
    .filter((b) => String(b.mode || '').toLowerCase() === 'info_block')
    .map((b) => String(b.payload?.bodyText || b.payload?.content || '').trim())
    .filter(Boolean)
    .join('\n\n');
}

/** Repara texto de completar irresoluble — solo cuando faltan datos; no reescribe contenido válido. */
export function repairCompletarText(text: string, options: RepairCompletarOptions = {}): string {
  const source = String(text || '').trim();
  if (!source) return source;

  const allowPercent =
    options.allowPercentRepair !== false &&
    (options.isGuide !== true ||
      isExplicitMathPercentTopic(options.subjectTag, options.topic));

  if (
    options.isGuide &&
    isHumanitiesGuideSubject(options.subjectTag, options.topic) &&
    looksLikePercentDrillCompletar(source)
  ) {
    const fromContext = options.guideContext
      ? buildContextualCompletarFromGuide(options.guideContext, options.index ?? 0)
      : null;
    if (fromContext) return fromContext;
  }

  if (allowPercent && isUnsolvablePercentCompletar(source)) {
    const seed = [options.title, options.printTitle, options.topic, source].filter(Boolean).join(' · ');
    const idx = options.index != null ? options.index : scenarioIndex(seed);
    return buildPercentVariationCompletarText(seed, idx);
  }

  return source;
}

export function shouldUsePercentFormulaLayout(_text: string, _title = ''): boolean {
  return false;
}

const FORMULA_FIELDS = [
  'formulaLayout',
  'formulaNumeratorLabel',
  'formulaDenominatorLabel',
  'formulaSuffix',
] as const;

function stripFormulaFields(payload: Record<string, unknown>): Record<string, unknown> {
  const next = { ...payload };
  for (const key of FORMULA_FIELDS) {
    delete next[key];
  }
  return next;
}

type GuideExamLike = {
  resourceType?: string;
  blocks: Array<{ mode: string; payload: Record<string, unknown> }>;
};

/**
 * Normaliza adjuntos de contexto en guías (fórmulas, recordatorios explícitos):
 * - Nunca inyecta fórmula por defecto.
 * - Quita formulaLayout de completar (solo info_block si el autor/LLM lo definió).
 * - Si hay fórmula en varios info_block, conserva solo el primero.
 */
export function normalizeGuideContextBlocks<T extends GuideExamLike>(exam: T): T {
  if (exam.resourceType !== 'guide') return exam;

  let formulaSeen = false;
  const blocks = exam.blocks.map((block) => {
    let payload = { ...block.payload };

    if (block.mode === 'completar') {
      return { ...block, payload: stripFormulaFields(payload) };
    }

    if (block.mode === 'info_block' && payload.formulaLayout === 'fraction-percent') {
      if (formulaSeen) {
        payload = stripFormulaFields(payload);
      } else {
        formulaSeen = true;
      }
    }

    return { ...block, payload };
  });

  return { ...exam, blocks };
}

/** @deprecated Usar normalizeGuideContextBlocks */
export function enforcePercentFormulaOnceInGuide<T extends GuideExamLike>(exam: T): T {
  return normalizeGuideContextBlocks(exam);
}

export function examLooksPercentVariationGuide(_exam: GuideExamLike): boolean {
  return false;
}

/** Reorienta completar de guías humanísticas: % genérico → hecho del info_block. */
export function normalizeGuideCompletarBlocks<T extends GuideExamLike & { subjectTag?: string; title?: string }>(
  exam: T,
): T {
  if (exam.resourceType !== 'guide') return exam;

  const guideContext = collectGuideContextFromBlocks(exam.blocks);
  if (!guideContext.trim()) return exam;

  const topic = String(exam.title || '').replace(/^gu[ií]a:\s*/i, '').trim();
  if (!isHumanitiesGuideSubject(exam.subjectTag, topic)) return exam;

  let completarIndex = 0;
  const blocks = exam.blocks.map((block) => {
    if (block.mode !== 'completar') return block;
    const payload = { ...block.payload };
    const text = String(payload.text || '').trim();
    if (!looksLikePercentDrillCompletar(text)) {
      completarIndex += 1;
      return block;
    }
    const repaired = repairCompletarText(text, {
      title: String(payload.title || ''),
      printTitle: payload.printTitle ? String(payload.printTitle) : undefined,
      topic,
      index: completarIndex,
      subjectTag: exam.subjectTag,
      isGuide: true,
      guideContext,
      allowPercentRepair: false,
    });
    completarIndex += 1;
    if (repaired === text) return block;
    return { ...block, payload: { ...payload, text: repaired } };
  });

  return { ...exam, blocks };
}
